import { CalendarClock, CalendarX } from "lucide-react";
import { format } from "date-fns";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { cn } from "@/lib/utils";

export function isRetired(
  retirementDate: string,
  now: Date = new Date()
): boolean {
  return new Date(retirementDate) <= now;
}

export function retirementLabel(
  retirementDate: string,
  now: Date = new Date()
): string {
  const when = format(new Date(retirementDate), "d MMM yyyy");
  return isRetired(retirementDate, now)
    ? `Retired on ${when}`
    : `Retiring on ${when}`;
}

export function retirementBadgeLabel(
  retirementDate: string,
  now: Date = new Date()
): string {
  const when = format(new Date(retirementDate), "d MMM");
  return isRetired(retirementDate, now) ? `Retired ${when}` : `Retires ${when}`;
}

export interface RetiringSoon {
  name: string;
  retirementDate: string;
  retired: boolean;
}

const HORIZON_DAYS = 30;

export function retiringSoon(
  items: Iterable<{ name: string; retirement_date?: string | null }>,
  now: Date = new Date()
): RetiringSoon[] {
  const horizon = new Date(now);
  horizon.setDate(horizon.getDate() + HORIZON_DAYS);
  const seen = new Map<string, RetiringSoon>();
  for (const item of items) {
    if (!item.retirement_date || seen.has(item.name)) continue;
    if (new Date(item.retirement_date) <= horizon) {
      seen.set(item.name, {
        name: item.name,
        retirementDate: item.retirement_date,
        retired: isRetired(item.retirement_date, now),
      });
    }
  }
  return [...seen.values()].sort((a, b) =>
    a.retirementDate.localeCompare(b.retirementDate)
  );
}

function count(n: number, noun: string): string {
  return `${n} ${n === 1 ? noun : `${noun}s`}`;
}

export function retiringSoonTitle(soon: RetiringSoon[]): string {
  const retired = soon.filter((m) => m.retired).length;
  const ahead = soon.length - retired;
  const parts: string[] = [];
  if (retired > 0) parts.push(`${count(retired, "model")} already retired`);
  if (ahead > 0) {
    parts.push(`${count(ahead, "model")} retiring in the next 30 days`);
  }
  return parts.join(", ");
}

export function retirementHint(
  retirementDate: string,
  replacedBy?: string | null,
  now: Date = new Date()
): string {
  const label = retirementLabel(retirementDate, now);
  const target = replacedBy ? `move to ${replacedBy}` : "move to a replacement";
  return isRetired(retirementDate, now)
    ? `${label}. The vendor no longer serves this model; ${target}.`
    : `${label}. The vendor stops serving this model on that day; ${target} before then.`;
}

export function RetirementNotice({
  retirementDate,
  replacedBy,
  size = 12,
  className,
}: {
  retirementDate?: string | null;
  /** Display name of the replacement, already resolved by the caller. */
  replacedBy?: string | null;
  size?: number;
  className?: string;
}) {
  if (!retirementDate) return null;
  const retired = isRetired(retirementDate);
  const Icon = retired ? CalendarX : CalendarClock;
  return (
    <NoxusTooltip
      content={retirementHint(retirementDate, replacedBy)}
      placement="top"
      classNameTrigger="flex items-center leading-none"
    >
      <Icon
        size={size}
        className={cn(
          "shrink-0",
          retired ? "text-red-600" : "text-warning-600",
          className
        )}
        aria-label={retirementLabel(retirementDate)}
        data-testid="retirement-notice"
      />
    </NoxusTooltip>
  );
}
